import { X, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';

const FILING_STATUS_LABELS = {
  single_or_mfs: 'Single or Married filing separately',
  married_jointly: 'Married filing jointly or Qualifying surviving spouse',
  head_of_household: 'Head of household',
};

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border/60 last:border-0">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-foreground font-medium text-right">{value}</span>
  </div>
);

// Read-only viewer for a submitted Form W-4 -- previously the compliance
// pill on TaxFormsTab just said "W-4 on file" with no way to actually see
// what was filed, for either a driver (fields live on their DriverProfile,
// captured at onboarding via StepTaxForms.jsx) or staff (a dedicated
// StaffW4Form row, captured via CompleteW4Gate.jsx). Callers normalize
// either shape into this same `w4` prop before opening it.
export default function ViewW4Modal({ personName, taxYear, w4, onClose }) {
  const step3Total = (Number(w4.dependentsQualifying || 0) * 2200)
    + (Number(w4.dependentsOther || 0) * 500)
    + Number(w4.otherCredits || 0);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg my-8 p-6 space-y-4 z-10 bg-background border border-border rounded-2xl shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="font-space font-semibold text-foreground text-lg flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" /> Form W-4 ({taxYear})
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">{personName}</p>

        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Personal Information</p>
          <Row label="Name" value={w4.name || '—'} />
          <Row label="Address" value={w4.address ? `${w4.address}, ${w4.city || ''} ${w4.state || ''} ${w4.zip || ''}`.trim() : '—'} />
          <Row label="SSN" value={w4.ssn ? `•••-••-${String(w4.ssn).replace(/\D/g, '').slice(-4)}` : '—'} />
          <Row label="Filing Status" value={w4.exempt ? 'Exempt' : (FILING_STATUS_LABELS[w4.filingStatus] || '—')} />
        </div>

        {!w4.exempt ? (
          <>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Step 2 — Multiple Jobs / Spouse Works</p>
              <Row label="Only two jobs total" value={w4.multipleJobs ? 'Yes' : 'No'} />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Step 3 — Dependents and Other Credits</p>
              <Row label="Qualifying children under 17" value={w4.dependentsQualifying || 0} />
              <Row label="Other dependents" value={w4.dependentsOther || 0} />
              <Row label="Other credits" value={money(w4.otherCredits)} />
              <Row label="Step 3 total" value={money(step3Total)} />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Step 4 — Other Adjustments</p>
              <Row label="Other income" value={money(w4.otherIncome)} />
              <Row label="Deductions" value={money(w4.deductions)} />
              <Row label="Extra withholding / pay period" value={money(w4.extraWithholding)} />
            </div>
          </>
        ) : (
          <p className="text-xs text-amber-500">Claimed exemption from withholding — Steps 2-4 skipped.</p>
        )}

        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Signature</p>
          {w4.signature ? (
            <img src={w4.signature} alt="Signature" className="w-full max-h-28 object-contain rounded-lg border border-border bg-white" />
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
          {w4.signedAt && <p className="text-[11px] text-muted-foreground mt-1">Signed {new Date(w4.signedAt).toLocaleString()}</p>}
        </div>

        <button
          onClick={onClose}
          className={cn("w-full px-4 py-2 rounded-lg border border-border text-sm text-foreground hover:bg-muted transition-colors")}
        >
          Close
        </button>
      </div>
    </div>
  );
}
